import type { ReactNode } from 'react';

/**
 * Tailwind-styled toggle switch. Created in Phase 7-dashboard-b for the candidate
 * dashboard preferences screen + "Willing to relocate" toggle on My Profile.
 *
 * Renders as a single `<button role="switch">` so the entire row (label, description,
 * track) is one click target. The earlier label-wrapping-checkbox structure caused a
 * white-screen crash inside ProfileCompletionModal when the click forwarded into a
 * deeply-nested form re-render — switching to a plain button removes the implicit
 * label/input association entirely.
 */
export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Required visible label. */
  label: ReactNode;
  /** Optional muted helper text shown under the label. */
  description?: ReactNode;
  disabled?: boolean;
  /** Optional id for the underlying button. */
  id?: string;
}

export function Switch({ checked, onChange, label, description, disabled, id }: SwitchProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onChange(!checked);
      }}
      className={[
        'flex items-start gap-4 w-full text-left',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 rounded-md',
      ].join(' ')}
    >
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-brand-primary">{label}</span>
        {description && (
          <span className="block text-xs text-brand-muted mt-1 leading-relaxed">{description}</span>
        )}
      </span>
      <span
        aria-hidden
        className={[
          'shrink-0 mt-0.5 inline-flex items-center w-10 h-6 rounded-full transition-colors',
          checked ? 'bg-brand-primary' : 'bg-gray-300',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block w-5 h-5 rounded-full bg-white shadow transform transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
          ].join(' ')}
        />
      </span>
    </button>
  );
}
